import * as React from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import strings from '../shared.translations';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { createUTCDate, printDateMonth, printDateMonthAndYear } from '@/helpers/dateHelper';
import { useMemo } from 'react';
import { useMonthDates } from './useMonthDates';

interface MonthSelectOption {
  name: string;
  value: any;
  disabled?: boolean;
}

interface MonthPickerProps {
  value: Date;
  onChange: (newValue: Date) => void;
  position?: 'left' | 'center';
  showLabels?: boolean;
}

export const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  onChange,
  position = 'center',
  showLabels = true
}) => {
  const prevMonth = () => {
    const newVal = new Date(value);

    //Go back a month.
    newVal.setUTCDate(1);
    newVal.setUTCMonth(newVal.getUTCMonth() - 1);
    newVal.setUTCHours(0, 0, 0, 0);

    onChange(newVal);
  };

  const nextMonth = () => {
    const newVal = new Date(value);

    //Go forwards a month.
    newVal.setUTCDate(1);
    newVal.setUTCMonth(newVal.getUTCMonth() + 1);
    newVal.setUTCHours(0, 0, 0, 0);

    onChange(newVal);
  };

  const { start, end } = useMonthDates(value);

  const startMonth = printDateMonthAndYear(start);
  const endMonth = printDateMonthAndYear(end);

  const monthDisplay = startMonth === endMonth ? `${startMonth}` : `${startMonth} - ${endMonth}`;

  const [selectedYear, setSelectedYear] = React.useState<number>(start.getUTCFullYear());

  const historyLimit = new Date(2025, 0, 1);

  const monthOptions: MonthSelectOption[] = useMemo(() => {
    const options: MonthSelectOption[] = [];
    for (let i = 0; i < 12; i++) {
      const monthStart = createUTCDate(selectedYear, i, 1);

      const disabled = historyLimit && monthStart < historyLimit;

      options.push({
        name: printDateMonth(monthStart),
        value: monthStart,
        disabled
      });
    }

    return options;
  }, [selectedYear, historyLimit]);

  const canGoBackMonth = useMemo(() => {
    const date = new Date(value);

    date.setUTCMonth(date.getUTCMonth() - 1);

    return !historyLimit || date >= historyLimit;
  }, [value, historyLimit]);

  const canGoBackYear = useMemo(() => {
    return !historyLimit || selectedYear > historyLimit.getUTCFullYear();
  }, [selectedYear, historyLimit]);

  return (
    <div className="flex justify-between items-stretch gap-5">
      <button
        type="button"
        aria-label="Previous Month"
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-2 rounded-lg border bg-white flex items-center gap-1"
        onClick={prevMonth}
        disabled={!canGoBackMonth}
      >
        <FiChevronLeft className="w-6 h-6" />
        {showLabels && <p className="hidden sm:block">{strings.date.previousMonth}</p>}
      </button>
      <Popover className="relative">
        <PopoverButton className="h-full py-3 border border-border rounded-md bg-white flex items-center leading-none px-3 gap-3">
          <FiCalendar />
          {monthDisplay}
        </PopoverButton>
        <PopoverPanel
          className="absolute border border-border bg-white p-5 z-50 shadow w-[400px]"
          style={{ left: position === 'center' ? 'calc(50% - 200px)' : undefined }}
        >
          {({ close }) => (
            <>
              <div className="flex gap-2 justify-center items-center">
                <button
                  onClick={canGoBackYear ? () => setSelectedYear(selectedYear - 1) : undefined}
                  disabled={!canGoBackYear}
                >
                  <FiChevronLeft className="w-6 h-6" style={{ color: !canGoBackYear ? 'grey' : undefined }} />
                </button>
                <div className="py-2 px-10 bg-active rounded-md overflow-hidden border border-border font-bold">
                  {selectedYear}
                </div>
                <button onClick={() => setSelectedYear(selectedYear + 1)}>
                  <FiChevronRight className="w-6 h-6" />
                </button>
              </div>
              <div className="grid grid-cols-4">
                {monthOptions.map(opt => (
                  <div
                    className="py-2 px-5 flex items-center justify-center"
                    style={{ color: opt.disabled ? 'grey' : undefined }}
                    key={opt.name}
                  >
                    <button
                      onClick={
                        opt.disabled
                          ? undefined
                          : () => {
                              onChange(opt.value);
                              close();
                            }
                      }
                      style={{ cursor: opt.disabled ? 'default' : 'pointer' }}
                    >
                      {opt.name}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </PopoverPanel>
      </Popover>
      <button
        type="button"
        aria-label="Next Month"
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-2 rounded-lg border bg-white flex items-center gap-1"
        onClick={nextMonth}
      >
        {showLabels && <p className="hidden sm:block">{strings.date.nextMonth}</p>}
        <FiChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
};
